import {DateTime} from 'luxon';
import {
  CreativeVerificationResult,
  DynamicParameter,
  RemoteDynamicParameter,
} from '../creatives/creatives.types';
import {
  DPDynamicSourceType,
  DynamicParametersType,
} from '../dynamicParameters/dynamicParameters.enums';
import {ProductType, ProductTypeName} from '../products/products.enums';
import {ClientShareInfo} from '../sharing/sharing.types';
import {TaxonomyType} from '../taxonomy/taxonomy.enums';

export type TemplateTypeName =
  | 'Display Ads'
  | 'Video Ads'
  | 'Image Ads'
  | 'Interactive Video Ads'
  | 'In-Banner Video Ads';

export enum CreativeTemplateType {
  DisplayAds = 0,
  VideoAds = 1,
  ImageAds = 2,
  InteractiveVideoAds = 4,
  InBannerVideoAds = 5,
}

export enum AiCreativeTemplateType {
  DisplayAd = 'HTML Ad',
  VideoAd = 'Video Ad',
  ImageAd = 'Image Ad',
  InteractiveVideoAd = 'Interactive Video Ad',
  InBannerVideoAd = 'In-Banner Video Ad',
}

export enum CreativeTemplateSource {
  AfterEffect = 'AfterEffects',
  ClinchSDK = 'ClinchSDK',
  AdBuilder = 'AdBuilder',
}

export enum CreativeTemplateSourceType {
  AfterEffect = 1,
  ClinchSDK = 2,
  AdBuilder = 6,
}

export enum AiCreativeTemplateSource {
  ClinchSDK = 'Custom Code',
  AdBuilder = 'Ad Builder',
  AfterEffects = 'After Effects',
}

export interface RemoteFormatInstance {
  Height: number;
  Width: number;
  aspectRatio: number;
  expHeight: number;
  expWidth: number;
}

export const templateTypeNamesByValue: Record<CreativeTemplateType, TemplateTypeName> = {
  [CreativeTemplateType.DisplayAds]: 'Display Ads',
  [CreativeTemplateType.ImageAds]: 'Image Ads',
  [CreativeTemplateType.VideoAds]: 'Video Ads',
  [CreativeTemplateType.InteractiveVideoAds]: 'Interactive Video Ads',
  [CreativeTemplateType.InBannerVideoAds]: 'In-Banner Video Ads',
};

export interface FormatInstance extends Omit<RemoteFormatInstance, 'formatString'> {
  formatString: string;
}

export interface RemoteFormat {
  Id: number;
  RendererVersion: number;
  duration: number;
  isMulti: false;
  itemCount: number;
  minItemCount: number;
  status: number;
  styleId: number;
  updateIndex: number;
  verificationResult: null | CreativeVerificationResult;
  format: RemoteFormatInstance;
}

export interface Format extends Omit<RemoteFormat, 'format'> {
  format: FormatInstance;
}

export enum CreativeTemplateFeature {
  Xenon = 'xenon',
  Location = 'location',
  Feed = 'product',
  Content = 'content',
  PromoSlide = 'promoSlide',
  Weather = 'weather',
  Video = 'video',
}

export enum TemplateType {
  DisplayAds = 0,
  VideoAds = 1,
  ImageAds = 2,
  ExternalAds = 3,
  InteractiveVideoAds = 4,
  InBannerVideoAds = 5,
}

export type CampaignsByCreative = Record<number, {campaignId: number; campaignName: string}[]>;

export interface TemplateCreative {
  id: number;
  name: string;
  clientId: number;
}

export interface RemoteCreativeTemplate {
  createTime: string;
  updateTime: string;
  createdByUserName: string;
  updatedByUserName: string;
  creativeIds: number[];
  creatives: TemplateCreative[];
  clientsNames?: string[];
  features?: CreativeTemplateFeature[];
  id: number;
  instructions: string;
  name: string;
  description: string;
  defaultProductFeedId: number;
  defaultLocationFeedId: number;
  defaultContentFeedId: number;
  poster: string;
  previewThumbnailUrl: string;
  previewVideoUrl?: string;
  productName?: string;

  dynamicParameters?: Record<string, RemoteDynamicParameter>;
  source: CreativeTemplateSource;

  formats?: RemoteFormat[];
  type: CreativeTemplateType;
  productType: ProductType;
  isDefaultEnabled?: boolean;
  clientShareInfo?: ClientShareInfo[];
  sourceToTargetClients?: ClientShareInfo[];
  creativeIdInActiveCampaigns: CampaignsByCreative;
}

export interface CreativeTemplate
  extends Omit<
    RemoteCreativeTemplate,
    'formats' | 'dynamicParameters' | 'createTime' | 'updateTime'
  > {
  formats: Format[];
  typeName: TemplateTypeName;
  productTypeName?: ProductTypeName;
  minItemsCount: number;
  maxItemsCount: number;
  isXenon: boolean;
  dynamicParameters: Record<string, DynamicParameter>;
  createTime: DateTime;
  updateTime: DateTime;
}

export interface RemoteDropDownOption {
  uiName: string;
  key: string;
  requiredConditionTypes: string[];
  conditionType?: string;
}

export interface DPDynamicSourceOption {
  uiName: string;
  key: string;
  requiredConditionTypes: string[];
  conditionType?: string;
  taxonomyKey?: string;
  taxonomized?: boolean;
  sourceType: DPDynamicSourceType;
  isDeprecated?: boolean;
}

export type DropDownMediaUrlKeys = 'videoUrls' | 'imageUrls' | 'audioUrls' | 'fonts' | 'subtitles';

export type DropDownOptionsKey =
  | 'numeric'
  | 'textText'
  | 'cond'
  | 'colors'
  | 'clickUrls'
  | DropDownMediaUrlKeys;

export type RemoteCreativeTemplateDropDownOptions = Record<
  DropDownOptionsKey,
  RemoteDropDownOption[]
>;

export type DPDynamicSourceOptions = Record<DropDownOptionsKey, DPDynamicSourceOption[]>;

export interface CreateTemplateFormRemote {
  name?: string;
  type?: number;
  productType?: number;
  defaultProductFeedId?: number;
  defaultLocationFeedId?: number;
  defaultContentFeedId?: number;
  locationSupport?: boolean;
  weatherSupport?: boolean;
  contentSupport?: boolean;
  description?: string;
  // Data?: string;
  instructions?: string;
  isDefaultEnabled?: boolean;
  source?: CreativeTemplateSource;
}

export interface AELayerAssetsMapping {
  fontDisplayName: string;
  compId: number;
  layerId: number;
  url: string;
}

export type RemoteDynamicParamNameOptions = Record<
  string,
  {Type: DynamicParametersType; Name: string}[]
>;
export type DynamicParamNameOptions = Record<
  string,
  {type: DynamicParametersType; label: string; value: string}[]
>;

export type CreativeTemplateDropDownOptions = RemoteCreativeTemplateDropDownOptions;
export type DropDownOption = RemoteDropDownOption;

export interface RemoteGeneratedDynamicParams {
  dynamicParameter: {
    category: string;
    name: string;
    type: DynamicParametersType;
    isTaxonomized: boolean;
    taxonomyType: TaxonomyType;
  };
  layers: {
    compId: number;
    layerId: number;
    mappingId: number;
    format: string;
  }[];
}

export interface GeneratedDynamicParams extends Omit<RemoteGeneratedDynamicParams, 'layers'> {
  layerIds: string[];
}
